import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import type { Agency, AgencyUser } from '@atlas/shared';
import { slugifyCompany } from '@atlas/shared';
import { listAgencyUsers } from '../lib/callables.js';
import { AgencyPicker } from '../components/AgencyPicker.js';

function message(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}

/** RFC 4180 quoting — only quote when the value actually needs it. */
function csvCell(value: string | null): string {
  const s = value ?? '';
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadUsersCsv(agency: Agency, users: AgencyUser[]): void {
  const header = ['email', 'atlas_id', 'name', 'status', 'onboarding_step'];
  const rows = users.map((u) =>
    [u.email, u.atlasId, u.name, u.status, u.onboardingStep].map(csvCell).join(','),
  );
  const blob = new Blob([[header.join(','), ...rows].join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${slugifyCompany(agency.name) || 'agency'}_users.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Looks up one agency's users in the production source Postgres: pick an agency
 * with the shared AgencyPicker (which searches the cached active-agency list),
 * then read its `users` rows. Read-only — `listAgencyUsers` only SELECTs, scoped
 * to the chosen agency by `agency_id`.
 */
export function AgencyUsersPage() {
  const [agency, setAgency] = useState<Agency | null>(null);
  const [users, setUsers] = useState<AgencyUser[] | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  useEffect(() => {
    if (!agency) {
      setUsers(null);
      setUsersError(null);
      return;
    }

    let cancelled = false;
    setLoadingUsers(true);
    setUsersError(null);
    listAgencyUsers({ agencyId: agency.id, includeDeleted })
      .then(({ data }) => {
        if (!cancelled) setUsers(data.users);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setUsers(null);
        setUsersError(message(err));
      })
      .finally(() => {
        if (!cancelled) setLoadingUsers(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agency, includeDeleted]);

  return (
    <div className="max-w-6xl mx-auto px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#37352f] tracking-tight">
          Agency users
        </h1>
        <p className="text-sm text-[#73726e] mt-1">
          Find an agency by name, then look up its users in the source database.
        </p>
      </div>

      <div className="max-w-md mb-8">
        <span className="text-xs font-medium text-[#73726e]">Agency</span>
        <div className="mt-1">
          <AgencyPicker value={agency} onChange={setAgency} />
        </div>
        {agency && (
          <span className="block text-xs text-[#aeaaa4] mt-1">
            Agency ID <code className="font-mono">{agency.id}</code>
          </span>
        )}
      </div>

      {agency && (
        <>
          <div className="flex items-center justify-between gap-4 mb-3">
            <div className="flex items-center gap-4">
              <h2 className="text-sm font-semibold text-[#37352f]">
                {agency.name}
                {users && (
                  <span className="ml-2 font-normal text-[#aeaaa4] tabular-nums">
                    {users.length} user{users.length === 1 ? '' : 's'}
                  </span>
                )}
              </h2>
              <label className="flex items-center gap-1.5 text-xs text-[#73726e] cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(e) => setIncludeDeleted(e.target.checked)}
                />
                Include deleted
              </label>
            </div>
            {users && users.length > 0 && (
              <button
                type="button"
                onClick={() => downloadUsersCsv(agency, users)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-[#37352f] border border-[#e9e9e7] rounded hover:bg-[#f1f1ef] transition-colors"
              >
                <Download size={12} /> CSV
              </button>
            )}
          </div>

          {usersError && (
            <p className="text-sm text-[#e03e3e] bg-[#fbe8e8] border border-[#f5c6c6] rounded px-3 py-2 mb-4">
              {usersError}
            </p>
          )}

          {loadingUsers ? (
            <div className="py-12 text-center text-sm text-[#aeaaa4]">Loading…</div>
          ) : users && users.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#aeaaa4]">
              No users found for this agency.
            </div>
          ) : users ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e9e9e7]">
                    {['Email', 'Atlas ID', 'Name', 'Status', 'Onboarding step'].map(
                      (h) => (
                        <th
                          key={h}
                          className="py-2 px-3 text-left text-xs font-medium text-[#aeaaa4] whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.atlasId}
                      className="border-b border-[#f1f1ef] hover:bg-[#fafafa] transition-colors"
                    >
                      <td className="py-2.5 px-3 font-medium text-[#37352f] whitespace-nowrap">
                        {u.email || '—'}
                      </td>
                      <td className="py-2.5 px-3 text-[#aeaaa4] font-mono text-xs whitespace-nowrap">
                        {u.atlasId}
                      </td>
                      <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                        {u.name ?? '—'}
                      </td>
                      <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                        {u.status ?? '—'}
                      </td>
                      <td className="py-2.5 px-3 text-[#73726e] whitespace-nowrap">
                        {u.onboardingStep ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
